import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed, ref, watch } from 'vue';
import type { IPrice } from '@/interface/IPrice';
import { PriceChangeStatus } from '@/interface/enum';
import { useAutoResetState } from '@/libraries/composables/useAutoResetState';
import { switchToEuroPrice } from '@/libraries/helpers/oddsHelper';
import { useAccountStore } from '@/stores/accountStore';

export function resolvePriceChangeStatus(newPrice: IPrice, oldPrice: IPrice): PriceChangeStatus {
    const normalizedNewPrice = switchToEuroPrice(newPrice.value, newPrice.priceStyle);
    const normalizedOldPrice = switchToEuroPrice(oldPrice.value, oldPrice.priceStyle);

    if (normalizedNewPrice > normalizedOldPrice) {
        return PriceChangeStatus.Rise;
    }
    if (normalizedNewPrice < normalizedOldPrice) {
        return PriceChangeStatus.Drop;
    }
    return PriceChangeStatus.Remain;
}

export function usePriceChange(price: Ref<IPrice | null>) {
    const [priceChangeStatus, setPriceChangeStatus] = useAutoResetState(PriceChangeStatus.Remain, 3000);
    const isPriceRise = computed(() => priceChangeStatus.value === PriceChangeStatus.Rise);
    const isPriceDrop = computed(() => priceChangeStatus.value === PriceChangeStatus.Drop);

    const { customerInfo } = storeToRefs(useAccountStore());
    const prevPriceStyle = ref(customerInfo.value.priceStyle);

    watch(price, (newVal, oldVal) => {
        if (customerInfo.value.priceStyle !== prevPriceStyle.value) {
            prevPriceStyle.value = customerInfo.value.priceStyle;
            return;
        }

        if (oldVal === null || newVal === null) {
            return;
        }

        const nextPriceChangeStatus = resolvePriceChangeStatus(newVal, oldVal);
        if (nextPriceChangeStatus !== PriceChangeStatus.Remain) {
            setPriceChangeStatus(nextPriceChangeStatus);
        }
    });

    return {
        priceChangeStatus,
        isPriceRise,
        isPriceDrop,
    };
}
